import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { JarallaxOptions } from 'jarallax';

import { PageBuilderElementWrapper } from 'src/components/PageBuilder/components/PageBuilderElementWrapper';
import { LazyPicture } from 'src/hooks/useLazyLoad';
import { RichText } from 'src/components/RichText/RichText';
import { getAdvancedSettings } from 'src/components/PageBuilder/components/advancedSettings';
import type { HeroType } from 'src/components/PageBuilder/components';
import { ButtonLink } from 'src/components/Button/ButtonLink';
import { useJarallax } from 'src/components/PageBuilder/components/hooks';

import classes from './Hero.module.scss';

type HeroProps = HeroType;

/**
 * Return the PB Slider
 * @param props
 * @constructor
 */
export function Hero(props: HeroProps) {
    /**
     * Configure the styles requires for the Block
     */
    const backgroundElement = useRef<HTMLDivElement | null>(null);
    const hasVideoOverlay = props.videoOverlayColor !== undefined;
    const videoOverlayStyles = {
        backgroundColor: props.videoOverlayColor,
    };

    /**
     * If the background type is video or parallax is enabled, we need to enable the parallax effect
     */
    const isParallaxEnabled = Boolean(props.backgroundType === 'video' || props.enableParallax);
    const parallaxSpeed = !props.enableParallax ? 1 : props.parallaxSpeed;

    const config: JarallaxOptions = {
        imgPosition: 'centre',
        imgRepeat: 'no-repeat',
        imgSize: 'cover',
        imgSrc: props.desktopImage,
        speed: parallaxSpeed,
        videoLazyLoading: props.videoLazyLoading,
        videoLoop: props.videoLoop,
        videoPlayOnlyVisible: props.videoPlayOnlyVisible,
        videoSrc: props.videoSrc,
    };

    useJarallax({
        isEnabled: isParallaxEnabled,
        backgroundElement,
        config,
        backgroundType: props.backgroundType,
    });

    const backgroundStyles = {
        backgroundColor: props.backgroundColor,
    };

    const advancedSettings = getAdvancedSettings<HeroProps>(props);

    const actionCount = props.actions ? props.actions.length : 0;
    const hasCtaOnly = !props.content;

    /**
     * Render the Divider Block UI
     */
    const HomeCarouselLazyImageWrapper = (props: { item: HeroProps }) => {
        const { item } = props;
        const [cta] = item.actions;

        if (cta && cta.url) {
            return (
                <Link to={cta.url} className={`${classes.contentLink} lozad`}>
                    <LazyPicture
                        data-alt={item.altText ?? ''}
                        className={classes.imageContainer}
                        data-iesrc={item.desktopImage ?? item.laptopImage ?? item.mobileImage ?? ''}
                    >
                        <source media="(min-width: 1200px)" srcSet={item.desktopImage} />
                        <source media="(min-width: 750px)" srcSet={item.laptopImage} />
                        <source media="(max-width: 750px)" srcSet={item.mobileImage} />
                    </LazyPicture>
                </Link>
            );
        }

        return (
            <LazyPicture
                data-alt={item.altText ?? ''}
                className={`${classes.imageContainer} lozad`}
                data-iesrc={item.desktopImage ?? item.laptopImage ?? item.mobileImage ?? ''}
            >
                <source media="(min-width: 1200px)" srcSet={item.desktopImage} />
                <source media="(min-width: 750px)" srcSet={item.laptopImage} />
                <source media="(max-width: 750px)" srcSet={item.mobileImage} />
            </LazyPicture>
        );
    };

    /**
     * Render the Divider Block as a banner or with a link wrapper
     */
    return (
        <PageBuilderElementWrapper
            contentType={props.contentType}
            uniqueID={props.uniqueID}
            styles={{
                ...advancedSettings,
                ...backgroundStyles,
            }}
            classNames={[classes.slide]}
            ref={backgroundElement}
        >
            {hasVideoOverlay && <div className={classes['videoOverlay']} style={videoOverlayStyles} />}

            {/*Will handle the rendering of the images or video*/}
            {props.backgroundType === 'image' && props.desktopImage && <HomeCarouselLazyImageWrapper item={props} />}

            {/*Handles the rendering of the content and its positioning*/}
            <div className={classes.content}>
                <div
                    className={classes.contentInfo}
                    data-ui-state-is-single={!props.hasMoreThanOneSlide}
                    data-ui-state-is-cta-only={hasCtaOnly}
                    data-ui-state-position={props.contentPosition}
                >
                    {props.content && (
                        <div className={classes.contentText}>
                            <RichText content={props.content} />
                        </div>
                    )}

                    <ul className={classes.contentActions} data-ui-state-length={actionCount}>
                        {props.actions.map((action) => {
                            if (action.url && action.text) {
                                return (
                                    <li>
                                        <ButtonLink
                                            key={action.url}
                                            to={action.url}
                                            className={classes.action}
                                            style={{
                                                backgroundColor: props.buttonBackgroundColour?.replace(/^"|"$/g, ''),
                                                color: props.buttonTextColour?.replace(/^"|"$/g, ''),
                                            }}
                                        >
                                            {action.text}
                                        </ButtonLink>
                                    </li>
                                );
                            }
                        })}
                    </ul>
                </div>
            </div>
        </PageBuilderElementWrapper>
    );
}
